import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  message,
} from 'antd';
import Table, { ColumnsType } from 'antd/es/table';
import { getUmsAdminLoginLog, ipBlacklistApi } from 'apis/sys';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import formatTime from 'utils/formatTime';

type UmsAdminLoginLog = Awaited<
  ReturnType<typeof getUmsAdminLoginLog>
>['list'][number];

const useQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [username, setUsername] = useQueryState(
    'username',
    parseAsString.withDefault(''),
  );
  const [ip, setIp] = useQueryState('ip', parseAsString.withDefault(''));
  const [success, setSuccess] = useQueryState('success', parseAsBoolean);
  const [startDate, setStartDate] = useQueryState(
    'startDate',
    parseAsString.withDefault(''),
  );
  const [endDate, setEndDate] = useQueryState(
    'endDate',
    parseAsString.withDefault(''),
  );

  return {
    pageNum,
    pageSize,
    username,
    ip,
    success,
    startDate,
    endDate,
    setPageNum,
    setPageSize,
    setUsername,
    setIp,
    setSuccess,
    setStartDate,
    setEndDate,
  };
};

const AdminLoginLogPage = () => {
  const {
    pageNum,
    pageSize,
    username,
    ip,
    success,
    startDate,
    endDate,
    setPageNum,
    setPageSize,
    setUsername,
    setIp,
    setSuccess,
    setStartDate,
    setEndDate,
  } = useQueryParams();

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<UmsAdminLoginLog>();

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);
      const { list, total } = await getUmsAdminLoginLog({
        pageNum,
        pageSize,
        username: username || undefined,
        ip: ip || undefined,
        success: success ?? undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      setDataSource(list);
      setTotal(total);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [
    endDate,
    ip,
    pageNum,
    pageSize,
    setDataSource,
    setLoading,
    setTotal,
    startDate,
    success,
    username,
  ]);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const dateRange = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    if (!startDate && !endDate) return null;
    return [
      startDate ? dayjs(startDate) : null,
      endDate ? dayjs(endDate) : null,
    ];
  }, [endDate, startDate]);

  const onBanIp = useCallback((banIp?: string) => {
    if (!banIp) return;

    Modal.confirm({
      title: '确认拉黑 IP',
      content: `确定将 ${banIp} 加入黑名单？拉黑后该 IP 将无法访问所有 API。`,
      onOk: async () => {
        await ipBlacklistApi.add({
          ip: banIp,
          remark: '来自登录日志',
        });
        message.success('已加入黑名单');
      },
    });
  }, []);

  const columns: ColumnsType<UmsAdminLoginLog> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 100,
      },
      {
        title: '用户名',
        dataIndex: 'username',
        key: 'username',
        render: (value?: string | null) => value || '-',
      },
      {
        title: '结果',
        dataIndex: 'success',
        key: 'success',
        width: 100,
        render: (value?: boolean) =>
          value ? (
            <Tag color="success">成功</Tag>
          ) : (
            <Tag color="error">失败</Tag>
          ),
      },
      {
        title: '登录时间',
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime?: string) => formatTime(createTime),
      },
      {
        title: '登录IP',
        dataIndex: 'ip',
        key: 'ip',
        render: (value?: string) => value || '-',
      },
      {
        title: '登录地址',
        dataIndex: 'address',
        key: 'address',
        render: (value?: string) => value || '-',
      },
      {
        title: '登录UA',
        dataIndex: 'userAgent',
        key: 'userAgent',
        ellipsis: true,
        render: (value?: string) => value || '-',
      },
      {
        title: '操作',
        key: 'action',
        fixed: 'right',
        width: 120,
        render: (_, record) => (
          <Button
            type="link"
            danger
            disabled={!record.ip}
            onClick={() => onBanIp(record.ip)}
          >
            拉黑 IP
          </Button>
        ),
      },
    ],
    [onBanIp],
  );

  return (
    <div className="space-y-4">
      <Card bodyStyle={{ padding: '16px' }}>
        <Form layout="inline">
          <Form.Item label="用户名">
            <Input
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              allowClear
            />
          </Form.Item>

          <Form.Item label="IP" className="w-60">
            <Input
              value={ip}
              onChange={(e) => {
                setIp(e.target.value);
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              allowClear
            />
          </Form.Item>

          <Form.Item label="结果" className="w-60">
            <Select
              value={
                success === null || success === undefined ? undefined : success
              }
              onChange={(value) => {
                setSuccess(value === undefined ? null : value);
                setPageNum(1);
              }}
              allowClear
              options={[
                { label: '成功', value: true },
                { label: '失败', value: false },
              ]}
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
            />
          </Form.Item>

          <Form.Item label="登录时间">
            <DatePicker.RangePicker
              showTime
              value={dateRange}
              onChange={(dates) => {
                setStartDate(
                  dates?.[0] ? dates[0].format('YYYY-MM-DD HH:mm:ss') : '',
                );
                setEndDate(
                  dates?.[1] ? dates[1].format('YYYY-MM-DD HH:mm:ss') : '',
                );
                setPageNum(1);
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                htmlType="button"
                onClick={() => {
                  setUsername('');
                  setIp('');
                  setSuccess(null);
                  setStartDate('');
                  setEndDate('');
                  setPageNum(1);
                  setPageSize(10);
                }}
              >
                <Trans i18nKey={LOCALS.reset} />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: '16px' }}>
        <Table
          bordered
          pagination={{
            total,
            pageSize,
            current: pageNum,
            onChange: (page, nextPageSize) => {
              setPageNum(page);
              setPageSize(nextPageSize);
            },
          }}
          loading={loading}
          rowKey="id"
          dataSource={dataSource}
          columns={columns}
          scroll={{ x: 'max-content' }}
          tableLayout="auto"
        />
      </Card>
    </div>
  );
};

export default AdminLoginLogPage;
